import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const scrollToTop = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    {
      name: "Packages",
      path: "/packages",
      dropdown: [
        { name: "All Packages", path: "/packages" },
        { name: "International", path: "/packages?category=international" },
        { name: "Domestic", path: "/packages?category=domestic" },
        { name: "Honeymoon", path: "/packages?type=honeymoon" },
        { name: "Family", path: "/packages?type=family" },
        { name: "Group Tours", path: "/packages?type=group" },
      ],
    },
    { name: "Theme Tours", path: "/theme-tours", noWrap: true },
    { name: "Weddingz", path: "/weddingz" },
    { name: "Corporate", path: "/corporate" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button onClick={() => scrollToTop("/")} className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="/assets/images/logo.png" 
              alt="Dnotours Logo" 
              className="h-16 md:h-20 w-auto"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) =>
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        location.pathname.startsWith(link.path)
                          ? "text-coral"
                          : "text-slate-700 hover:text-coral"
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {link.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white border-slate-200">
                    {link.dropdown.map((item) => (
                      <DropdownMenuItem key={item.name} asChild>
                        <button
                          onClick={() => scrollToTop(item.path)}
                          className="w-full text-left text-slate-700 hover:text-coral focus:text-coral cursor-pointer"
                          data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {item.name}
                        </button>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : link.highlight ? (
                <button
                  key={link.name}
                  onClick={() => scrollToTop(link.path)}
                  className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.path
                      ? "text-slate-900 font-semibold"
                      : "text-slate-900 hover:text-coral"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.name}
                </button>
              ) : (
                <button
                  key={link.name}
                  onClick={() => scrollToTop(link.path)}
                  className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.path
                      ? "text-coral"
                      : "text-slate-700 hover:text-coral"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.name}
                </button>
              )
            )}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              onClick={() => scrollToTop("/get-quote")}
              className="bg-coral hover:bg-coral-dark text-white h-10 px-6 rounded-full font-medium"
              data-testid="header-get-quote-btn"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-700 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <nav className="container-custom py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <button
                    onClick={() => scrollToTop(link.path)}
                    className={`block w-full text-left px-4 py-3 text-base font-medium transition-colors ${
                      location.pathname === link.path
                        ? "text-coral bg-coral/5 rounded-lg"
                        : "text-slate-700 hover:text-coral"
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </button>
                  {link.dropdown && (
                    <div className="pl-4 mt-1 space-y-1">
                      {link.dropdown.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => scrollToTop(item.path)}
                          className="block w-full text-left px-4 py-2 text-sm text-slate-600 hover:text-coral"
                          data-testid={`mobile-dropdown-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-4 px-4">
                <Button
                  onClick={() => scrollToTop("/get-quote")}
                  className="bg-coral hover:bg-coral-dark text-white w-full rounded-full"
                  data-testid="mobile-get-quote-btn"
                >
                  Get Quote
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
